import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2, Users } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { ProfileLink } from '@/components/profile/ProfileLink';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getProfileUrl } from '@/types/profile';
import { cn } from '@/lib/utils';

interface InterestedPerson {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  image: string | null;
  status: string | null;
}

interface InterestedResponse {
  interested: InterestedPerson[];
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

function personName(person: InterestedPerson) {
  return `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'Unknown User';
}

interface OpenLeadInterestedListProps {
  leadId: string;
}

export function OpenLeadInterestedList({ leadId }: OpenLeadInterestedListProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { communityId } = useParams();
  const { data, isLoading, error } = useSWR<InterestedResponse>(
    isOpen ? `/api/leads/${leadId}/interested` : null,
    fetcher
  );

  const interested = data?.interested ?? [];

  const openProfile = (userId: string) => {
    let href = getProfileUrl({ userId });
    if (communityId && href.startsWith('/')) {
      href = `/${communityId}${href}`;
    }
    navigate(href);
  };

  return (
    <div className="w-full space-y-3">
      <Button
        type="button"
        variant="outline"
        className="w-full sm:w-auto rounded-full py-5 px-5 text-sm font-semibold shadow-sm"
        onClick={() => setIsOpen((open) => !open)}
      >
        <Users className="w-4 h-4 mr-2" />
        {isOpen ? 'Hide Interested' : 'View Interested'}
      </Button>

      {isOpen && (
        <div className="rounded-2xl border border-border/50 bg-card/60 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <Table>
            <TableHeader className="bg-accent/5">
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-bold text-xs uppercase tracking-widest">
                  Author
                </TableHead>
                <TableHead className="font-bold text-xs uppercase tracking-widest">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={2} className="h-24 text-center">
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading interested people...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={2} className="h-24 text-center text-sm text-muted-foreground">
                    Unable to load interested people.
                  </TableCell>
                </TableRow>
              ) : interested.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="h-24 text-center text-sm text-muted-foreground">
                    No one has shown interest yet.
                  </TableCell>
                </TableRow>
              ) : (
                interested.map((person) => (
                  <TableRow
                    key={person.userId}
                    className={cn(
                      'border-border/40 cursor-pointer hover:bg-accent/5 transition-colors'
                    )}
                    onClick={() => openProfile(person.userId)}
                  >
                    <TableCell className="py-3">
                      <ProfileLink
                        userId={person.userId}
                        image={person.image || undefined}
                        name={personName(person)}
                        avatarSize="sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell className="py-3 text-sm font-medium text-foreground/90">
                      {person.status || 'Not Contacted Yet'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
